
import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

interface AdminAuthContextType {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  isAdmin: boolean;
}

const AdminAuthContext = createContext<AdminAuthContextType>({
  user: null,
  session: null,
  isLoading: true,
  isAdmin: false
});

export const AdminAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const requestSequenceRef = useRef(0);

  const resolveAdminState = async (currentSession: Session | null) => {
    const requestId = ++requestSequenceRef.current;

    setSession(currentSession);
    setUser(currentSession?.user ?? null);

    if (!currentSession?.user) {
      if (requestId !== requestSequenceRef.current) return;
      setIsAdmin(false);
      setIsLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase.rpc('is_admin', { user_id: currentSession.user.id });

      if (requestId !== requestSequenceRef.current) return;

      if (error) {
        console.error("AdminAuth: erro ao verificar perfil admin", error);
        setIsAdmin(false);
        setIsLoading(false);
        return;
      }

      setIsAdmin(Boolean(data));
      setIsLoading(false);
    } catch (error) {
      if (requestId !== requestSequenceRef.current) return;
      console.error("AdminAuth: exceção ao verificar admin", error);
      setIsAdmin(false);
      setSession(null);
      setUser(null);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const relevantEvents = new Set(['INITIAL_SESSION', 'SIGNED_IN', 'USER_UPDATED']);

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, currentSession) => {
        if (event === 'SIGNED_OUT') {
          requestSequenceRef.current += 1;
          setSession(null);
          setUser(null);
          setIsAdmin(false);
          setIsLoading(false);
          return;
        }

        if (relevantEvents.has(event)) {
          setIsLoading(true);
          void resolveAdminState(currentSession);
        } else {
          setSession(currentSession);
          setUser(currentSession?.user ?? null);
        }
      }
    );

    const initSession = async () => {
      try {
        const { data: { session: currentSession } } = await supabase.auth.getSession();
        setIsLoading(true);
        await resolveAdminState(currentSession);
      } catch (error) {
        console.error("AdminAuth: erro ao verificar sessão inicial", error);
        setSession(null);
        setUser(null);
        setIsAdmin(false);
        setIsLoading(false);
      }
    };

    initSession();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const value = {
    user,
    session,
    isLoading,
    isAdmin,
  };

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
};

export function useAdminAuth() {
  const context = useContext(AdminAuthContext);
  
  if (!context) {
    throw new Error("useAdminAuth must be used within an AdminAuthProvider");
  }
  
  return context;
}
